import { useState, useEffect } from "react";
import { TRowItem } from "../../../components/Table/TableRow";
import { useMangeTableItems } from "../../../hooks/useManageTableItems";
import { usePagination } from "../../../hooks/usePagination";
import {
  AssetDistributionRejectionLog,
  IPagination,
  IQueryPerams,
  PageActions,
} from "../../../config/types";
import { formatDate } from "../../../utils/common";
import { MainPageBase } from "../../page-bases/MainPageBase";
import { Box, Stack } from "@mui/material";
import DamsTable from "../../../components/Table/CollapsibleTable";
import { BadgeLabel } from "../../../components/Label";
import useAppNavigation from "../../../hooks/useAppNavigation";
import appRoutePath from "../../../config/path";
import { distributionRejectionApi } from "../../../services/distribution/distribution-rejection";
import { AssetDistributionRejectionFilter } from "../../../common-ui/page-filters/AssetDistributionRejectionFilter";
import { NoItemContent } from "../../page-bases/NoItemContent";
import { DamsEmptyPageProps } from "../../page-bases";
import { ExportModal } from "../../../components/Modals/ExportModal";
import ActionModal from "../../../components/Modals/ActionModal";
import { DamsAppAsset } from "../../../assets/registry";
import { usePageModels } from "../../../hooks/usePageActions";
import { appConfig } from "../../../config/config";

interface FiltersState {
  search?: string;
  asset_brand?: string;
  asset_serial_number?: string;
  asset_embossment_number?: string;
  distribution_batch_number?: string;
  from_location?: string;
  to_location?: string;
  user_email?: string;
  reason?: string;
  is_deleted: boolean;
}

const DistributionRejectionsLogsPage = () => {
  const [pageState, setPageState] = useState("main");
  const [emptyState, setEmptyState] = useState<DamsEmptyPageProps>();
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const [pageAction, updatePageAction] = useState<PageActions>();

  const [currentFilters, setCurrentFilters] = useState<FiltersState>({
    is_deleted: false
  });

  const [showExportModal, setShowExportModal] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  const { models, closeModel, openModel } = usePageModels();

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);
  
  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<AssetDistributionRejectionLog[]>(
    distributionRejectionApi.getRejectedDistributions
  );

  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });
  const navigate = useAppNavigation();

  const populateDataSet = (
    data: AssetDistributionRejectionLog[],
    pagination?: IPagination
  ) => {
    const formatted_data = data.map((record) => ({
      ...record,
      asset_brand: `${record.asset_brand}(${record.asset_model})`,
      user_email: `${record.user_email}  (${
        record.user_first_name + record.user_last_name
      })`,
      created_at: formatDate(record.created_at),
      from_location: `${record.from_location_domain} (${record.from_location})`,
      to_location: `${record.to_location_domain} (${record.to_location})`,
    }));
    setDataset(formatted_data);
    updatePaginationInitState(pagination);
    setPageState("main");
  };

  const fetchData = async (query: IQueryPerams = {}) => {
    try {
      const result = await distributionRejectionApi.getRejectedDistributions({
        ...query,
        is_deleted: false
      });
      if (result?.data) {
        populateDataSet(result.data, result.pagination);
        setCurrentFilters(prev => ({ ...prev, ...query }));
      }
    } catch (err) {
      setEmptyState({ type: "empty-db" });
      setPageState("empty-db");
    }
  };

  useEffect(() => {
    fetchData();
    resetSelectedRowItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function clearSearch() {
    setPageState("main");
    setQuerySet({ is_deleted: false });
    fetchData({ is_deleted: false });
  }

  const page_info = getEmptyPageInfo(pageState);

  function openExportModal() {
    setShowExportModal(true);
  }

  function closeExportModal() {
    setShowExportModal(false);
  }

  async function handleExportCSV() {
    setIsExportingCSV(true);
    try {
      const response = await distributionRejectionApi.exportRejectedDistributions({
        file_format: 'csv',
        ...querySet
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split('/').pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace('/api/v1', '')}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: 'GET'
          });

          if (!downloadResponse.ok) {
            throw new Error('Download failed');
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || 'distribution_rejections_export.csv';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "CSV file has been downloaded successfully.",
            success: true
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, '_blank');

          updatePageAction({
            title: "Export Initiated",
            message: "CSV export has been initiated. Check your downloads folder.",
            success: true
          });
        }

        openModel("alert");
      } else {
        throw new Error('Invalid response from export API');
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "CSV export failed. Please try again.",
        success: false
      });
      openModel("alert");
    } finally {
      setIsExportingCSV(false);
      closeExportModal();
    }
  }

  async function handleExportPDF() {
    setIsExportingPDF(true);
    try {
      const response = await distributionRejectionApi.exportRejectedDistributions({
        file_format: 'pdf',
        ...querySet
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split('/').pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace('/api/v1', '')}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: 'GET'
          });

          if (!downloadResponse.ok) {
            throw new Error('Download failed');
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || 'distribution_rejections_export.pdf';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "PDF file has been downloaded successfully.",
            success: true
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, '_blank');

          updatePageAction({
            title: "Export Initiated",
            message: "PDF export has been initiated. Check your downloads folder.",
            success: true
          });
        }

        openModel("alert");
      } else {
        throw new Error('Invalid response from export API');
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "PDF export failed. Please try again.",
        success: false
      });
      openModel("alert");
    } finally {
      setIsExportingPDF(false);
      closeExportModal();
    }
  }

  function toggleAlertModal() {
    closeModel();
  }

  const ExtraRowContent = ({ item }: { item: TRowItem }) => {
    const goDetailsPage = () => {
      navigate({
        path: appRoutePath.REJECTED_INBOUND_ASSET_DISTRIBUTIONS_DETAIL_PAGE,
        params: {
          id: item.id,
        },
      });
    };
    return (
      <Stack alignItems={"center"} direction={"row"} spacing={1}>
        <BadgeLabel
          onClick={goDetailsPage}
          title={"Details"}
          tip="Click to view more details"
          colorType="blue"
        />
      </Stack>
    );
  };

  return (
    <MainPageBase
      pageState={pageState}
      title={"Asset Distribution Rejection History"}
      subtitle={
        "A comprehensive log of all rejected asset distribution requests, including the reason for rejection, asset details, distribution information, and user information."
      }
      exportBtnOnClick={openExportModal}
      exportBtnText={"Export"}
    >
      <Box
        height={`100%`}
        display={"flex"}
        alignItems={pageState !== "main" ? "center" : ""}
        justifyContent={"center"}
      >
        {pageState === "empty-db" ? (
          <NoItemContent
            type={pageState}
            title={page_info.title}
            subtitle={page_info.message}
            clearSearchBtn={() => clearSearch()}
          />
        ) : (
          <Stack width={"100%"} spacing={1}>
            <AssetDistributionRejectionFilter<AssetDistributionRejectionLog[]>
              ftn={distributionRejectionApi.getRejectedDistributions}
              updateDataSet={populateDataSet}
              updateQuerySet={setQuerySet}
            />

            <DamsTable
              tableTitle="Asset Distribution Rejections"
              tableSubtitle="List of all asset distribution requests that were rejected, with detailed context."
              numTableRecords="25"
              tableHeaders={columns}
              tableKeys={tkeys}
              data={dataset}
              maxRowCells={6}
              hiddenKeys={hkeys}
              hiddenLabels={hcolumns}
              useTableCheckbox
              onTableItemSelect={toggleRow}
              toggleSelectAllRow={toggleSelectAllRow}
              selectedRowItems={selectedRowItems}
              hasPagination
              paginationState={pagination}
              onNextPage={paginateToNextPage(querySet, populateDataSet)}
              onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
              setActivePage={paginateToPageNumber(populateDataSet, querySet)}
              extraRowComponent={ExtraRowContent}
              tableTitleBadge={selectedRowItems.length === 0 ? undefined : selectedRowItems.length + ' selected'}
            />

            {/* Export Modal */}
            <ExportModal
              show={showExportModal}
              onClose={closeExportModal}
              onExportCSV={handleExportCSV}
              onExportPDF={handleExportPDF}
              isExportingCSV={isExportingCSV}
              isExportingPDF={isExportingPDF}
              title="Export Distribution Rejections"
              subtitle="Choose your preferred format to export asset distribution rejection data"
            />

            {/* Alert Modal */}
            <ActionModal
              show={models.showAlertModel}
              title={pageAction?.title ?? ""}
              leftIcon={
                pageAction?.success === false
                  ? DamsAppAsset.icons.warning
                  : undefined
              }
              subtitle={pageAction?.message ?? ""}
              onLeftBtnPress={closeModel}
              leftBtnTxt={"close"}
              onRightBtnPress={toggleAlertModal}
              rightBtnTxt={"continue"}
              onClosePress={closeModel}
            />
          </Stack>
        )}
      </Box>
    </MainPageBase>
  );
};

const tkeys: (keyof AssetDistributionRejectionLog)[] = [
  "asset_brand",
  "asset_embossment_number",
  "asset_serial_number",
  "from_location",
  "to_location",
  "created_at",
];

const columns = [
  "Brand (Model)",
  "Asset Embossment Number",
  "Asset Serial Number",
  "From - Domain (Location) ",
  "To - Domain (Location) ",
  "Date Assigned",
];

const hkeys: (keyof AssetDistributionRejectionLog)[] = [
  "reason",
  "distribution_batch_number",
  "distribution_batch_status",
  "distribution_batch_date",
  "user_email",
];

const hcolumns = [
  "reason",
  "Distribution Batch Number",
  "Distribution Batch Status",
  "Distribution Batch Date",
  "User- Email ( Name )",
];

const getEmptyPageInfo = (key: string): { title: string; message: string } => {
  if (page_msg.hasOwnProperty(key)) {
    return page_msg[key];
  } else {
    return { title: "", message: "" };
  }
};

const page_msg: { [key: string]: { title: string; message: string } } = {
  "empty-db": {
    title: "No Distribution Rejections Found",
    message:
      'No asset distribution rejections have been recorded yet.'
  },
  "empty-search": {
    title: "No Distribution Rejections Found",
    message:
      "No distribution rejections found matching your search criteria. Try adjusting your filters."
  }
};

export default DistributionRejectionsLogsPage;